import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { LeasePremiseUse, PremiseUseInput, SimilarMatch } from '../types';
import type { SaveResult } from '../api';

type Props = {
  initial?: LeasePremiseUse | null;
  save: (input: PremiseUseInput, force: boolean) => Promise<SaveResult>;
  onSuccess: () => void;
  onCancel: () => void;
  submitLabel: string;
};

type FormShape = { name: string; active: boolean };

export const PremiseUseForm = ({ initial, save, onSuccess, onCancel, submitLabel }: Props) => {
  const { register, handleSubmit, formState } = useForm<FormShape>({
    defaultValues: {
      name: initial?.name ?? '',
      active: initial?.active ?? true,
    },
  });
  const [similar, setSimilar] = useState<SimilarMatch[]>([]);

  const onValid = async (data: FormShape, force: boolean) => {
    const result = await save({ name: data.name.trim(), active: data.active }, force);
    if (result.ok) onSuccess();
    else setSimilar(result.similar);
  };

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit((d) => onValid(d, false))(e);
      }}
    >
      <div className="form-grid">
        <div className="field">
          <label>Name *</label>
          <input
            {...register('name', {
              required: true,
              onChange: () => setSimilar([]),
            })}
          />
        </div>
        <div className="field">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" {...register('active')} />
            Active
          </label>
        </div>
      </div>
      {similar.length > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: '1px solid #d9a300',
            background: '#fff8e6',
            borderRadius: 6,
          }}
        >
          <strong>Similar premise uses already exist:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {similar.map((s) => (
              <li key={s.id}>
                {s.name} ({Math.round(s.score * 100)}% match)
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 8, fontSize: 13, color: '#665100' }}>
            Edit the name to avoid a duplicate, or save anyway.
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        {similar.length > 0 && (
          <button
            type="button"
            disabled={formState.isSubmitting}
            onClick={(e) => {
              void handleSubmit((d) => onValid(d, true))(e);
            }}
          >
            Save anyway
          </button>
        )}
        <button type="submit" className="primary" disabled={formState.isSubmitting}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
};
